import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { auth } from '../services/api';
import { useAuthContext } from '../contexts/AuthContext';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuthContext();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const token = searchParams.get('token');

    if (!token) {
      setError('Authorization token not found');
      setIsLoading(false);
      return;
    }

    const handleCallback = async () => {
      try {
        const response = await auth.callbackAuthLink(token);

        if (response.success && response.data) {
          // При использовании куков сервер устанавливает их автоматически
          // Нам нужно только обновить локальное состояние
          login(response.data.email, response.data.name, response.data.company_name);

          setIsSuccess(true);

          // Перенаправляем на главную страницу через 2 секунды
          setTimeout(() => {
            navigate('/');
          }, 2000);
        } else {
          setError(response.error?.message || 'Authorization error');
        }
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Authorization error');
      } finally {
        setIsLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, login]);

  if (isLoading) {
    return (
      <div className="auth-callback">
        <div className="callback-content">
          <span className="ds-spinner ds-spinner--lg loading-spinner" aria-hidden="true" />
          <h2>Authorization...</h2>
          <p>Please wait</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="auth-callback error-auth-callback"
        style={{
          background: '#fff',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          className="callback-content"
          style={{
            background: '#fff',
            borderRadius: 20,
            boxShadow: '0 4px 32px rgba(66,133,244,0.10)',
            padding: 40,
            minWidth: 340,
            textAlign: 'center',
          }}
        >
          <div className="error-icon" style={{ color: '#EA4335', fontSize: 48, marginBottom: 16 }}>
            ✗
          </div>
          <h2 style={{ color: '#222', fontWeight: 700, marginBottom: 8 }}>Authorization Error</h2>
          <p style={{ color: '#888', fontSize: 16, marginBottom: 24 }}>{error}</p>
          <button
            type="button"
            className="submit-button"
            style={{
              background: '#4285F4',
              color: '#fff',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 16,
              padding: '10px 0',
              width: '100%',
              boxShadow: '0 2px 8px rgba(66,133,244,0.08)',
            }}
            onClick={() => navigate('/')}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div
        className="auth-callback success-auth-callback"
        style={{
          background: '#fff',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          className="callback-content"
          style={{
            background: '#fff',
            borderRadius: 20,
            boxShadow: '0 4px 32px rgba(66,133,244,0.10)',
            padding: 40,
            minWidth: 340,
            textAlign: 'center',
          }}
        >
          <div className="success-icon" style={{ color: '#FF8B00', fontSize: 48, marginBottom: 16 }}>
            ✓
          </div>
          <h2 style={{ color: '#222', fontWeight: 700, marginBottom: 8 }}>Authorization Successful!</h2>
          <p style={{ color: '#4285F4', fontSize: 18, marginBottom: 24 }}>You will be redirected to the main page...</p>
          <div style={{ height: 4, width: 80, background: '#FF8B00', borderRadius: 2, margin: '0 auto' }}></div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;
